/**
 * WarehouseQuickAddPage — mobile-first publish-from-warehouse UI.
 *
 * Operator opens this on their phone while standing in a Costco /
 * Ross / TJ Maxx aisle. Workflow:
 *   1. Tap "Add photo" → camera or library
 *   2. Pick the source store from the dropdown
 *   3. Type brand + title (English from the tag is fine)
 *   4. Type the $ price from the warehouse tag
 *   5. Hit "Generate" → Sonnet writes 2-sentence Russian description
 *   6. Hit "Publish" → goes straight to the chosen channel
 *
 * Target end-to-end time: ~30s per item. The expensive scrape +
 * vision-QA stages from the catalog pipeline don't apply here —
 * source IS the operator photo, so there's nothing to QA.
 *
 * Mobile design:
 *   - Single column, full-width inputs (no side-by-side)
 *   - Touch targets ≥ 44px (iOS HIG minimum)
 *   - No hover states (don't render usefully on touch)
 *   - Sticky publish button at bottom so it's always reachable
 *   - File inputs use `capture="environment"` so iOS opens camera by
 *     default; library picker stays one tap away
 */
import { useState, useMemo, useRef, useCallback, lazy, Suspense } from "react";
import { api } from "../api.js";
import { T_NEW } from "../theme.js";
import { Btn, Mono, Serif } from "./ui.jsx";
import { useToast } from "./toast.jsx";
import { useT } from "../i18n.jsx";
import { WAREHOUSE_CHANNELS } from "./warehouseChannels.js";
import { WarehousePriceWorklist } from "./WarehousePriceWorklist.jsx";

// Lazy: the scanner pulls in ZXing (~115 KB gzipped), which most sessions
// never use. Code-split it into its own chunk loaded on first Scan tap.
// vite-plugin-pwa still precaches the chunk, so it works offline in-aisle
// after the first app load. (Named export → map to default.)
const BarcodeScanner = lazy(() =>
  import("./BarcodeScanner.jsx").then((m) => ({ default: m.BarcodeScanner })));

export function WarehouseQuickAddPage({ onClose, onPublished }) {
  const t = useT();
  const toast = useToast();

  // "add" = capture a new item; "prices" = bulk-update existing prices.
  const [mode, setMode] = useState("add");

  // Form state
  const [channelSlug, setChannelSlug] = useState(() => {
    // Validate the remembered slug against the current list — the old
    // generic "costco" default is now a hidden review bucket (migration
    // 0067), so a stale value must fall back to a real warehouse channel.
    const saved = localStorage.getItem("cg_quickadd_lastChannel");
    return WAREHOUSE_CHANNELS.some(c => c.slug === saved) ? saved : WAREHOUSE_CHANNELS[0].slug;
  });
  const [photos, setPhotos] = useState([]); // [{ path, url, name }]
  const [brand, setBrand] = useState("");
  const [title, setTitle] = useState("");
  const [priceUsd, setPriceUsd] = useState("");
  const [msrpUsd, setMsrpUsd] = useState("");
  // "Sale valid through" date (Costco promos; migration 0069). Shown on the card.
  const [saleValidThrough, setSaleValidThrough] = useState("");
  // Costco item number off the shelf tag — optional, but it's the key the
  // coupon-book matcher uses to bulk-apply instant-savings later.
  const [itemNumber, setItemNumber] = useState("");
  // Product barcode (UPC/EAN). Both this and the item # can be filled by
  // the camera Scan button, which routes by barcode type.
  const [upc, setUpc] = useState("");
  const [scanning, setScanning] = useState(false);
  // Dedup hint: set when a scan matches an already-listed post.
  const [dupWarning, setDupWarning] = useState(null); // { code, title, publicCode } | null

  // A scan routes by format: a product UPC/EAN fills the barcode field, a
  // Code-128 shelf tag fills the item # field. After filling, we look the
  // code up to warn if it's already listed (dedup). useCallback keeps the
  // scanner's effect from re-running (it depends on this prop).
  const handleScan = useCallback(async (text, category) => {
    setScanning(false);
    const code = (text || "").trim();
    if (category === "product") {
      setUpc(code);
      toast.success(t("scan.filled.barcode", { code }));
    } else {
      setItemNumber(code);
      toast.success(t("scan.filled.item", { code }));
    }
    // Dedup check — non-fatal; a failure just skips the hint.
    try {
      const existing = await api.getPosts({ code });
      if (existing && existing.length) {
        const p = existing[0];
        setDupWarning({ code, title: p.title || "", publicCode: p.publicCode || "" });
      } else {
        setDupWarning(null);
      }
    } catch { /* ignore — dedup is best-effort */ }
  }, [toast, t]);
  const [sourceEn, setSourceEn] = useState("");             // EN — canonical source (migration 0070)
  const [description, setDescription] = useState("");       // RU
  const [descriptionEs, setDescriptionEs] = useState("");   // ES (migration 0068)
  const [notes, setNotes] = useState(""); // internal note, not published

  // UI state
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const cameraInputRef = useRef(null);
  const libraryInputRef = useRef(null);

  const channel = useMemo(
    () => WAREHOUSE_CHANNELS.find(c => c.slug === channelSlug) || WAREHOUSE_CHANNELS[0],
    [channelSlug],
  );

  // Validation — the publish button is enabled only when these pass.
  // Photos are optional in the model (a typed-only post still
  // publishes) but in practice the operator always has at least one.
  const priceCents = useMemo(() => {
    const n = parseFloat(priceUsd);
    return Number.isFinite(n) && n > 0 ? Math.round(n * 100) : null;
  }, [priceUsd]);
  const msrpCents = useMemo(() => {
    const n = parseFloat(msrpUsd);
    return Number.isFinite(n) && n > 0 ? Math.round(n * 100) : null;
  }, [msrpUsd]);
  const canPublish = !!(
    photos.length > 0 &&
    brand.trim() &&
    title.trim() &&
    priceCents !== null &&
    !publishing
  );

  // Sticky channel choice across sessions — operator usually does a
  // run of items from the same store before switching.
  function selectChannel(slug) {
    setChannelSlug(slug);
    localStorage.setItem("cg_quickadd_lastChannel", slug);
  }

  async function handleFileSelect(e) {
    const files = Array.from(e.target.files || []);
    e.target.value = ""; // reset so re-picking the same file fires onChange
    if (!files.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of files) {
        const r = await api.uploadImage(f);
        uploaded.push({ path: r.path, url: r.url, name: f.name });
      }
      setPhotos(prev => [...prev, ...uploaded].slice(0, 4)); // cap at 4 photos per post
    } catch (err) {
      toast.error(t("quickAdd.upload.failed", { err: err.message || String(err) }));
    }
    setUploading(false);
  }

  function removePhoto(idx) {
    setPhotos(prev => prev.filter((_, i) => i !== idx));
  }

  async function handleGenerate() {
    // English copy is the preferred source; fall back to brand/title.
    if (!sourceEn.trim() && !brand.trim() && !title.trim()) {
      toast.error(t("quickAdd.generate.needFields"));
      return;
    }
    setGenerating(true);
    try {
      const r = await api.generateQuickDescription({
        brand: brand.trim(),
        title: title.trim(),
        sourceDescription: sourceEn.trim() || undefined,
        notes: notes.trim() || undefined,
        channelSlug,
      });
      setDescription(r.descriptionRu || "");
      setDescriptionEs(r.descriptionEs || "");
    } catch (err) {
      toast.error(t("quickAdd.generate.failed", { err: err.message || String(err) }));
    }
    setGenerating(false);
  }

  async function handlePublish() {
    if (!canPublish) return;
    setPublishing(true);
    try {
      // Auto-translate the English source on save: if the operator typed
      // English but hasn't generated RU yet, translate now so the post is
      // trilingual without a separate Generate tap. Non-destructive — we
      // only fill RU/ES that are still empty. English is saved as bodyEn.
      const en = sourceEn.trim();
      let bodyRu = description.trim();
      let bodyEs = descriptionEs.trim();
      if (en && (!bodyRu || !bodyEs)) {
        try {
          const r = await api.generateQuickDescription({
            brand: brand.trim(), title: title.trim(),
            sourceDescription: en, channelSlug,
          });
          if (!bodyRu) bodyRu = (r.descriptionRu || "").trim();
          if (!bodyEs) bodyEs = (r.descriptionEs || "").trim();
        } catch (err) {
          // Don't lose the operator's work — publish English-only and let
          // the feed's English fallback cover RU/ES viewers until edited.
          toast.error(t("quickAdd.publish.failed", { err: err.message || String(err) }));
        }
      }
      await api.createPost({
        channelSlug,
        title: title.trim(),
        bodyRu: bodyRu || title.trim(),
        bodyEs: bodyEs || null,
        bodyEn: en || null,
        // Operator photos are plain product photos (not baked cards), so the
        // feed must render the title/price/description as live text over them.
        showText: true,
        priceCents,
        originalPriceCents: msrpCents,
        currency: "USD",
        brand: brand.trim(),
        category: null,
        itemNumber: itemNumber.trim() || null,
        upc: upc.trim() || null,
        saleValidThrough: saleValidThrough || null,
        images: photos.map(p => p.path),
        source: `warehouse:${channelSlug}`,
        sourceUrl: null,
        status: "published",
      });
      toast.success(t("quickAdd.published"));
      // Reset form for the next item but keep the channel choice and
      // photos cleared (operator is moving to the next aisle).
      setPhotos([]);
      setBrand("");
      setTitle("");
      setPriceUsd("");
      setMsrpUsd("");
      setSaleValidThrough("");
      setItemNumber("");
      setUpc("");
      setDupWarning(null);
      setSourceEn("");
      setDescription("");
      setDescriptionEs("");
      setNotes("");
      if (onPublished) onPublished();
    } catch (err) {
      toast.error(t("quickAdd.publish.failed", { err: err.message || String(err) }));
    }
    setPublishing(false);
  }

  // Touch-friendly input style — 44px min height, larger font than
  // the desktop default so iOS doesn't auto-zoom on focus (zoom
  // triggers when input font-size < 16px).
  const inp = {
    width: "100%",
    minHeight: 44,
    padding: "10px 14px",
    fontSize: 16,
    fontFamily: T_NEW.fontSans,
    color: T_NEW.ink,
    background: T_NEW.paper,
    border: `1px solid ${T_NEW.ink12}`,
    borderRadius: T_NEW.r2,
    boxSizing: "border-box",
  };

  return (
    <div style={{
      display: "flex",
      flexDirection: "column",
      gap: 14,
      paddingBottom: 80, // breathing room above the sticky publish button
    }}>
      {/* Camera barcode scanner (full-screen overlay, lazy-loaded) */}
      {scanning && (
        <Suspense fallback={
          <div style={{
            position: "fixed", inset: 0, zIndex: 9999, background: "#000",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "#fff", fontSize: T_NEW.meta, fontFamily: T_NEW.fontMono,
          }}>
            {t("scan.starting")}
          </div>
        }>
          <BarcodeScanner onResult={handleScan} onClose={() => setScanning(false)} />
        </Suspense>
      )}

      {/* Mode tabs — capture a new item vs bulk-update existing prices */}
      <div style={{ display: "flex", gap: 8 }}>
        {[["add", t("worklist.tab.add")], ["prices", t("worklist.tab.prices")]].map(([m, label]) => {
          const active = mode === m;
          return (
            <button key={m} onClick={() => setMode(m)}
              style={{
                flex: 1, minHeight: 40, borderRadius: T_NEW.r2, cursor: "pointer",
                border: `1px solid ${active ? T_NEW.persimmon : T_NEW.ink12}`,
                background: active ? T_NEW.persimmon : "transparent",
                color: active ? "#fff" : T_NEW.ink,
                fontFamily: T_NEW.fontMono, fontSize: T_NEW.micro, fontWeight: 600,
                textTransform: "uppercase", letterSpacing: T_NEW.monoTrack,
              }}>
              {label}
            </button>
          );
        })}
      </div>

      {mode === "prices" ? <WarehousePriceWorklist /> : (<>
      {/* Dedup hint — a scan matched an already-listed post */}
      {dupWarning && (
        <div style={{
          display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10,
          padding: "10px 12px", borderRadius: T_NEW.r2,
          background: T_NEW.persimmonSoft, border: `1px solid ${T_NEW.persimmon}`,
        }}>
          <div style={{ minWidth: 0 }}>
            <Mono size={T_NEW.micro} color={T_NEW.persimmon} style={{ fontWeight: 700 }}>
              {t("scanfind.dupTitle")}
            </Mono>
            <div style={{ fontSize: T_NEW.meta, color: T_NEW.ink, marginTop: 2,
                          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {dupWarning.title || dupWarning.code}
              {dupWarning.publicCode ? ` · ${dupWarning.publicCode}` : ""}
            </div>
            <div style={{ fontSize: T_NEW.micro, color: T_NEW.ink56, marginTop: 2 }}>
              {t("scanfind.dupHint")}
            </div>
          </div>
          <button onClick={() => setDupWarning(null)}
            style={{ flex: "0 0 auto", minHeight: 32, padding: "4px 10px", cursor: "pointer",
                     border: "none", background: "transparent", color: T_NEW.persimmon,
                     fontFamily: T_NEW.fontMono, fontSize: T_NEW.micro, fontWeight: 600 }}>
            ✕
          </button>
        </div>
      )}

      {/* Store picker — first decision, biggest impact on prompt */}
      <div>
        <Mono color={T_NEW.ink56} style={{ display: "block", marginBottom: 6 }}>
          {t("quickAdd.store")}
        </Mono>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {WAREHOUSE_CHANNELS.map(c => {
            const isActive = c.slug === channelSlug;
            return (
              <button key={c.slug} onClick={() => selectChannel(c.slug)}
                      style={{
                        minHeight: 36, padding: "6px 12px",
                        border: `1px solid ${isActive ? T_NEW.persimmon : T_NEW.ink12}`,
                        background: isActive ? T_NEW.persimmon : "transparent",
                        color: isActive ? "#fff" : T_NEW.ink,
                        borderRadius: 18,
                        fontFamily: T_NEW.fontMono,
                        fontSize: T_NEW.micro,
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: T_NEW.monoTrack,
                        cursor: "pointer",
                      }}>
                {c.label}
              </button>
            );
          })}
        </div>
        <p style={{ color: T_NEW.ink56, fontSize: T_NEW.micro, marginTop: 6, lineHeight: 1.5 }}>
          {channel.register === "spec"
            ? t("quickAdd.register.spec")
            : t("quickAdd.register.editorial")}
        </p>
      </div>

      {/* Photos — camera-first, library fallback */}
      <div>
        <Mono color={T_NEW.ink56} style={{ display: "block", marginBottom: 6 }}>
          {t("quickAdd.photos")}
        </Mono>
        {photos.length > 0 && (
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(80px, 1fr))",
            gap: 8,
            marginBottom: 10,
          }}>
            {photos.map((p, i) => (
              <div key={i} style={{ position: "relative", aspectRatio: "1 / 1" }}>
                <img src={p.url} alt=""
                     style={{ width: "100%", height: "100%", objectFit: "cover",
                              borderRadius: T_NEW.r1, border: `1px solid ${T_NEW.ink12}` }} />
                <button onClick={() => removePhoto(i)}
                        style={{
                          position: "absolute", top: 2, right: 2,
                          width: 24, height: 24, borderRadius: 12,
                          background: "rgba(20,17,13,0.7)", color: "#fff",
                          border: "none", cursor: "pointer",
                          fontSize: 14, lineHeight: 1,
                          display: "flex", alignItems: "center", justifyContent: "center",
                        }}>
                  ×
                </button>
              </div>
            ))}
          </div>
        )}

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {/* Camera button — `capture` opens the rear camera on iOS/Android */}
          <input ref={cameraInputRef} type="file" accept="image/*" capture="environment"
                 multiple onChange={handleFileSelect} style={{ display: "none" }} />
          {/* Library button — no capture attribute → opens photo library */}
          <input ref={libraryInputRef} type="file" accept="image/*"
                 multiple onChange={handleFileSelect} style={{ display: "none" }} />
          <Btn variant="outline" disabled={uploading || photos.length >= 4}
               onClick={() => cameraInputRef.current?.click()}
               style={{ flex: 1, minHeight: 44 }}>
            📷 {t("quickAdd.camera")}
          </Btn>
          <Btn variant="outline" disabled={uploading || photos.length >= 4}
               onClick={() => libraryInputRef.current?.click()}
               style={{ flex: 1, minHeight: 44 }}>
            🖼 {t("quickAdd.library")}
          </Btn>
        </div>
        {photos.length >= 4 && (
          <p style={{ color: T_NEW.ink56, fontSize: T_NEW.micro, marginTop: 4 }}>
            {t("quickAdd.photos.max")}
          </p>
        )}
        {uploading && (
          <p style={{ color: T_NEW.ink56, fontSize: T_NEW.meta, marginTop: 6 }}>
            {t("quickAdd.uploading")}
          </p>
        )}
      </div>

      {/* Brand + title */}
      <div>
        <Mono color={T_NEW.ink56} style={{ display: "block", marginBottom: 6 }}>
          {t("quickAdd.brand")}
        </Mono>
        <input value={brand} onChange={e => setBrand(e.target.value)}
               placeholder="Carmex"
               autoCapitalize="words" autoComplete="off"
               style={inp} />
      </div>

      <div>
        <Mono color={T_NEW.ink56} style={{ display: "block", marginBottom: 6 }}>
          {t("quickAdd.title")}
        </Mono>
        <input value={title} onChange={e => setTitle(e.target.value)}
               placeholder="Daily Care SPF 15 Lip Balm Sticks, 3-Count"
               autoCapitalize="sentences" autoComplete="off"
               style={inp} />
      </div>

      {/* Prices — side-by-side at this width since they're short */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <div>
          <Mono color={T_NEW.ink56} style={{ display: "block", marginBottom: 6 }}>
            {t("quickAdd.priceUsd")}
          </Mono>
          <input type="number" inputMode="decimal" step="0.01" min="0"
                 value={priceUsd} onChange={e => setPriceUsd(e.target.value)}
                 placeholder="7.97"
                 style={inp} />
        </div>
        <div>
          <Mono color={T_NEW.ink56} style={{ display: "block", marginBottom: 6 }}>
            {t("quickAdd.msrpUsd")}
          </Mono>
          <input type="number" inputMode="decimal" step="0.01" min="0"
                 value={msrpUsd} onChange={e => setMsrpUsd(e.target.value)}
                 placeholder="11.99"
                 style={inp} />
        </div>
      </div>

      {/* Sale valid-through date (Costco promos) — renders "valid through
          <date>" on the card. Leave blank for an open-ended price. */}
      <div>
        <Mono color={T_NEW.ink56} style={{ display: "block", marginBottom: 6 }}>
          {t("edit.validThrough")}
        </Mono>
        <input type="date" value={saleValidThrough}
               onChange={e => setSaleValidThrough(e.target.value)} style={inp} />
      </div>

      {/* Identifiers. The camera Scan fills item # or barcode by what it
          reads (Code-128 shelf tag → item #; product UPC/EAN → barcode);
          both stay hand-editable. The item # lets the coupon book auto-
          update this price; the barcode backs scan-to-find + dedup. */}
      <div>
        <Btn variant="outline" onClick={() => setScanning(true)}
             style={{ minHeight: 44, width: "100%", marginBottom: 10 }}>
          📷 {t("quickAdd.scan")}
        </Btn>
        <Mono color={T_NEW.ink56} style={{ display: "block", marginBottom: 6 }}>
          {t("quickAdd.itemNumber")}
        </Mono>
        <input value={itemNumber} onChange={e => setItemNumber(e.target.value)}
               inputMode="numeric" autoComplete="off"
               placeholder="1670512"
               style={inp} />
        <p style={{ color: T_NEW.ink56, fontSize: T_NEW.micro, marginTop: 4 }}>
          {t("quickAdd.itemNumber.hint")}
        </p>
      </div>

      <div>
        <Mono color={T_NEW.ink56} style={{ display: "block", marginBottom: 6 }}>
          {t("quickAdd.upc")}
        </Mono>
        <input value={upc} onChange={e => setUpc(e.target.value)}
               inputMode="numeric" autoComplete="off"
               placeholder="0 00000 00000 0"
               style={inp} />
        <p style={{ color: T_NEW.ink56, fontSize: T_NEW.micro, marginTop: 4 }}>
          {t("quickAdd.upc.hint")}
        </p>
      </div>

      {/* English description — the canonical source. Saved as bodyEn and
          auto-translated to RU + ES on save (or right now via Translate). */}
      <div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 6 }}>
          <Mono color={T_NEW.ink56} style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {t("quickAdd.descriptionEn")}
          </Mono>
          <Btn variant="outline" disabled={generating || (!sourceEn.trim() && !brand.trim() && !title.trim())}
               onClick={handleGenerate}
               style={{ flex: "0 0 auto", minHeight: 36, padding: "6px 12px", fontSize: T_NEW.meta }}>
            {generating ? t("edit.translating") : "✨ " + t("edit.translate")}
          </Btn>
        </div>
        <textarea value={sourceEn} onChange={e => setSourceEn(e.target.value)}
                  rows={3} placeholder={t("quickAdd.description.placeholder")}
                  style={{ ...inp, resize: "vertical", minHeight: 80, lineHeight: 1.5 }} />
        <p style={{ color: T_NEW.ink56, fontSize: T_NEW.micro, marginTop: 4 }}>
          {t("quickAdd.descriptionEn.hint")}
        </p>
      </div>

      {/* Russian description — auto-filled from English; editable. */}
      <div>
        <Mono color={T_NEW.ink56} style={{ display: "block", marginBottom: 6 }}>{t("quickAdd.description")}</Mono>
        <textarea value={description} onChange={e => setDescription(e.target.value)}
                  rows={3} placeholder={t("quickAdd.description.placeholder")}
                  style={{ ...inp, resize: "vertical", minHeight: 80, lineHeight: 1.5 }} />
      </div>

      {/* Spanish description — Generate fills both; editable. */}
      <div>
        <Mono color={T_NEW.ink56} style={{ display: "block", marginBottom: 6 }}>
          {t("edit.descriptionEs")}
        </Mono>
        <textarea value={descriptionEs} onChange={e => setDescriptionEs(e.target.value)}
                  rows={3} placeholder={t("quickAdd.description.placeholder")}
                  style={{ ...inp, resize: "vertical", minHeight: 80, lineHeight: 1.5 }} />
      </div>

      {/* Internal note — not published, for the operator's own
          reference (e.g., "tag ending .97, last-stock at warehouse 234") */}
      <div>
        <Mono color={T_NEW.ink56} style={{ display: "block", marginBottom: 6 }}>
          {t("quickAdd.notes")}
        </Mono>
        <input value={notes} onChange={e => setNotes(e.target.value)}
               placeholder={t("quickAdd.notes.placeholder")}
               style={inp} />
        <p style={{ color: T_NEW.ink56, fontSize: T_NEW.micro, marginTop: 4 }}>
          {t("quickAdd.notes.hint")}
        </p>
      </div>

      {/* Sticky publish action — always reachable on small screens */}
      <div style={{
        position: "sticky",
        bottom: 0,
        background: T_NEW.paper,
        marginTop: 8,
        padding: "12px 0 8px",
        borderTop: `1px solid ${T_NEW.ink12}`,
        display: "flex",
        gap: 10,
      }}>
        {onClose && (
          <Btn variant="outline" onClick={onClose} style={{ minHeight: 48, flex: "0 0 auto" }}>
            {t("quickAdd.cancel")}
          </Btn>
        )}
        <Btn variant="accent" disabled={!canPublish}
             onClick={handlePublish}
             style={{ minHeight: 48, flex: 1, fontSize: 16, fontWeight: 600 }}>
          {publishing ? t("quickAdd.publishing") : t("quickAdd.publish")}
        </Btn>
      </div>
      </>)}
    </div>
  );
}
